import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Modal from "../../components/common/modal";
import Button from "../../components/common/button";
import { userValidationSchema } from "../../validation/userValidation";
import "react-country-state-city/dist/react-country-state-city.css";
import { CountrySelect } from "react-country-state-city";
import FallbackImage from "../common/fallbackImage";

const emptyValues = {
  profilePicture: "",
  name: "",
  email: "",
  phone: "",
  country: "",
  designation: "",
};

const UserModal = ({ isOpen, onClose, user, mode, onSave, isLoading }) => {
  const [preview, setPreview] = useState("");
  const [countrySelectKey, setCountrySelectKey] = useState(0);
  const isCreateMode = mode === "create" && !user;

  const viewMode = mode === "view";

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: isCreateMode
      ? emptyValues
      : {
          profilePicture: user?.profilePicture || "",
          name: user?.name || "",
          email: user?.email || "",
          phone: user?.phone || "",
          country: user?.country || "",
          designation: user?.designation || "",
        },

    validationSchema: userValidationSchema,

    onSubmit: async (values, { resetForm }) => {
      const payload = new FormData();

      if (values.profilePicture && typeof values.profilePicture !== "string") {
        payload.append("profilePicture", values.profilePicture);
      }

      payload.append("name", values.name || "");
      payload.append("email", values.email || "");
      payload.append("phone", values.phone || "");
      payload.append("country", values.country || "");
      payload.append("designation", values.designation || "");


      await onSave(payload);

      resetForm();
      setPreview("");
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    if (isCreateMode) {
      formik.resetForm({ values: emptyValues });
      setPreview("");
      setCountrySelectKey((k) => k + 1);
      return;
    }

    setCountrySelectKey((k) => k + 1);

    if (user?.profilePicture) {
      setPreview(user.profilePicture);
    } else {
      setPreview("");
    }
  }, [isOpen, isCreateMode, user]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    formik.setFieldValue("profilePicture", file);

    setPreview(URL.createObjectURL(file));
  };

  const handleCountryChange = (country) => {
    formik.setFieldValue("country", country.name);
    formik.setFieldTouched("country", true);
    setCountrySelectKey((k) => k + 1);
    requestAnimationFrame(() => {
      document.body.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        mode === "delete"
          ? "Delete User"
          : viewMode
            ? "View User"
            : user
              ? "Edit User"
              : "Invite User"
      }
    >
      {mode === "delete" ? (
        <>
          <div style={{ padding: "10px 2px" }}>
            <p style={{ margin: 0, color: "#333" }}>
              Are you sure you want to delete{" "}
              <strong>{user?.name || "this user"}</strong>?
            </p>

            <p
              style={{
                marginTop: "8px",
                color: "#666",
                fontSize: "13px",
              }}
            >
              This action cannot be undone.
            </p>
          </div>

          <div className="modal-footer">
            <Button onClick={onClose} className="CancelButton">
              Cancel
            </Button>

            <Button
              onClick={() => onSave?.()}
              disabled={isLoading}
              className="CommonButton"
            >
              {isLoading ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="form-container">
            {/* Profile Picture */}
            <div className="form-group">
              <label htmlFor="imageUpload">Profile Picture</label>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                }}
              >
                {preview && (
                  preview.startsWith("blob:") ? (
                    <img
                      src={preview}
                      alt="Preview"
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <FallbackImage
                      src={preview}
                      alt="Preview"
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  )
                )}

                {!viewMode && (
                  <input
                    type="file"
                    id="imageUpload"
                    accept=".png,.jpg,.jpeg"
                    onChange={handleFileChange}
                  />
                )}
              </div>

              {formik.touched.profilePicture &&
                formik.errors.profilePicture && (
                  <p className="error-text">{formik.errors.profilePicture}</p>
                )}
            </div>

            {/* Name + Email */}
            <div className="form-row">
              <div className="form-group">
                <label>User Name</label>

                <input
                  type="text"
                  name="name"
                  autoComplete="off"
                  placeholder="user name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  readOnly={viewMode}
                />

                {formik.touched.name && formik.errors.name && (
                  <p className="error-text">{formik.errors.name}</p>
                )}
              </div>

              <div className="form-group">
                <label>Email</label>

                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="contact@example.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  readOnly={viewMode}
                />

                {formik.touched.email && formik.errors.email && (
                  <p className="error-text">{formik.errors.email}</p>
                )}
              </div>
            </div>

            {/* Phone + Country */}
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>

                <input
                  type="text"
                  name="phone"
                  placeholder="+91 9876543210"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  readOnly={viewMode}
                />

                {formik.touched.phone && formik.errors.phone && (
                  <p className="error-text">{formik.errors.phone}</p>
                )}
              </div>

              <div className="form-group">
                <label>Country</label>

                <CountrySelect
                  key={`country-select-${countrySelectKey}`}
                  placeHolder="Select Country"
                  defaultValue={formik.values.country}
                  onChange={handleCountryChange}
                  onBlur={() => formik.setFieldTouched("country", true)}
                />

                {formik.touched.country && formik.errors.country && (
                  <p className="error-text">{formik.errors.country}</p>
                )}
              </div>
            </div>

            {/*  Designation */}
            <div className="form-row">
              
              <div className="form-group">
                <label>Designation</label>

                <input
                  type="text"
                  name="designation"
                  placeholder="e.g. Admin"
                  value={formik.values.designation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  readOnly={viewMode}
                />

                {formik.touched.designation && formik.errors.designation && (
                  <p className="error-text">{formik.errors.designation}</p>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <Button type="button" onClick={onClose} className="CancelButton">
              {viewMode ? "Close" : "Cancel"}
            </Button>

            {!viewMode && (
              <Button
                type="submit"
                className="CommonButton"
                disabled={isLoading}
              >
                {mode === "edit" ? "Update User" : "Save User"}
              </Button>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
};

export default UserModal;
